import { inject, Injectable } from "@angular/core";
import { sntTranslate } from "src/app/support/translate";
import { marker } from "@jsverse/transloco-keys-manager/marker";
import { Customer } from "../backend/models/customer/customer";
import { Pet } from "src/app/backend/models/pet/pet";
import { formatAddress } from "src/app/common/format/address-format";
import { last, startCase, toLower, toPairs, uniq } from "lodash-es";
import { dateUtil, dateUtilDurationHumanize } from "src/app/support/date-util";
import { User } from "src/app/backend/models/user/user";
import { Tenant } from "src/app/backend/models/tenant/tenant";
import { ClinicalDocumentModule } from "src/app/backend/models/document";
import { getCustomerNominative } from "src/app/support/customer";
import { TemplateService } from "src/app/services/template.service";
import { StorageBackend } from "src/app/backend/api/storage/storage.backend";
import { defer, forkJoin, from, iif, lastValueFrom, of } from "rxjs";
import { catchError, switchMap } from "rxjs/operators";
import { blobToDataUrl } from "src/app/support/blob";
import { PrintService } from "src/app/services/print/print.service";
import { Recovery } from "src/app/backend/models/recovery";
import { Ticket } from "src/app/backend/models/ticket";
import { Reminder } from "src/app/backend/models/reminder";
import {
  LETTERHEAD_FREE,
  LetterheadBackend
} from "src/app/backend/api/letterhead/letterhead.backend";
import { Physician } from "src/app/backend/models/physician";
import {
  Letterhead,
  LetterheadLight
} from "src/app/backend/models/letterhead/letterhead";
import { CoatBackend } from "../backend/api/coat.backend";
import { TagBackend } from "../backend/api/tag/tag.backend";

type DocumentTypesFactory = () => {};
type DocumentTypes = Record<string, DocumentType>;
type DocumentType = {
  value: string;
  label: string;
};

type DocumentModulesFactory = () => DocumentModules;
type DocumentModules = Record<ClinicalDocumentModule, DocumentModule>;
type DocumentModule = {
  label: string;
  properties: Record<string, DocumentProperty>;
};
type DocumentProperty = {
  label: string;
  value: string;
  computable?: (data: DocumentDataModel) => boolean;
  computableValue?: string;
  computablePropertyName?: string;
  resolve?: (data: DocumentDataModel) => string;
};

export type DocumentDataModel = {
  tenant?: Tenant;
  user?: User;
  customer?: Customer;
  patient?: Pet;
  patientReferringPhysician: Physician | undefined;
  recovery?: Recovery;
  ticket?: Ticket;
  reminder?: Pick<Reminder, "expiresAt" | "scheduledAt"> & { name: string };
};

export type DocumentDataModelProvider = () => Promise<DocumentDataModel>;

const TYPES: DocumentTypesFactory = () => {
  return {
    ["privacy-policy"]: {
      value: "privacy-policy",
      label: sntTranslate(marker("Informativa privacy"))
    },
    certificate: {
      value: "certificate",
      label: sntTranslate(marker("Certificato"))
    },
    recovery: {
      value: "recovery",
      label: sntTranslate(marker("Ricovero"))
    },
    decease: {
      value: "decease",
      label: sntTranslate(marker("Decesso"))
    },
    unknown: {
      value: "unknown",
      label: sntTranslate(marker("Generico"))
    }
  };
};

const MODULES: DocumentModulesFactory = () => {
  return {
    clinic: {
      label: sntTranslate(marker("Clinica")),
      properties: {
        name: {
          label: sntTranslate(marker("Nome")),
          value: "clinic.name",
          resolve: (data: DocumentDataModel) => data.tenant?.name
        },
        city: {
          label: sntTranslate(marker("Città")),
          value: "clinic.city",
          resolve: (data) => data.tenant?.address?.city
        }
      }
    },
    user: {
      label: sntTranslate(marker("Utente")),
      properties: {
        firstname: {
          label: sntTranslate(marker("Nome")),
          value: "user.firstname",
          resolve: (data) => data.user?.firstName
        },
        lastname: {
          label: sntTranslate(marker("Cognome")),
          value: "user.lastname",
          resolve: (data) => data.user?.lastName
        },
        professionalRegister: {
          label: sntTranslate(marker("Iscrizione all'albo")),
          value: "user.professionalRegister",
          resolve: (data) => {
            return `Dott. ${data.user?.firstName || ""} ${
              data.user?.lastName || ""
            } iscritto all'ordine di ${
              data.user?.professionalRegisterProvince || ""
            } con numero ${data.user?.professionalRegisterNumber || ""}`;
          }
        }
      }
    },
    customer: {
      label: sntTranslate(marker("Contatto")),
      properties: {
        number: {
          label: sntTranslate(marker("Numero")),
          value: "customer.number",
          resolve: (data) => data.customer?.number
        },
        firstname: {
          label: sntTranslate(marker("Nome")),
          value: "customer.firstname",
          resolve: (data: DocumentDataModel) => data.customer?.firstName
        },
        lastname: {
          label: sntTranslate(marker("Cognome")),
          value: "customer.lastname",
          resolve: (data: DocumentDataModel) => data.customer?.lastName
        },
        fiscalCode: {
          label: sntTranslate(marker("Codice fiscale")),
          value: "customer.fiscalCode",
          resolve: (data: DocumentDataModel) => data.customer?.ssn
        },
        birthday: {
          label: sntTranslate(marker("Data di nascita")),
          value: "customer.birthday",
          resolve: (data) =>
            data.customer?.birthday
              ? dateUtil(data.customer?.birthday).format("DD/MM/YYYY")
              : undefined
        },
        vatCode: {
          label: sntTranslate(marker("Partita IVA")),
          value: "customer.vatCode",
          resolve: (data: DocumentDataModel) => data.customer?.vatNumber
        },
        businessName: {
          label: sntTranslate(marker("Ragione sociale")),
          value: "customer.businessName",
          resolve: (data: DocumentDataModel) => data.customer?.businessName
        },
        phone: {
          label: sntTranslate(marker("Telefono")),
          value: "customer.phone",
          resolve: (data: DocumentDataModel) => data.customer?.phone
        },
        email: {
          label: sntTranslate(marker("Email")),
          value: "customer.email",
          resolve: (data) => data.customer?.email
        },
        addressFull: {
          label: sntTranslate(marker("Indirizzo (completo)")),
          value: "customer.addressFull",
          resolve: (data) => formatAddress(data.customer?.address)
        },
        addressRoute: {
          label: sntTranslate(marker("Indirizzo")),
          value: "customer.addressRoute",
          resolve: (data) => data.customer.address?.address
        },
        addressCap: {
          label: sntTranslate(marker("CAP")),
          value: "customer.addressCap",
          resolve: (data) => `${data.customer?.address?.zipCode || ""}`
        },
        addressCity: {
          label: sntTranslate(marker("Comune")),
          value: "customer.addressCity",
          resolve: (data) => `${data.customer?.address?.city || ""}`
        },
        addressState: {
          label: sntTranslate(marker("Provincia")),
          value: "customer.addressState",
          resolve: (data) => `${data.customer?.address?.state || ""}`
        }
      }
    },
    patient: {
      label: sntTranslate(marker("Paziente")),
      properties: {
        number: {
          label: sntTranslate(marker("Numero")),
          value: "patient.number",
          resolve: (data) => data.patient?.number
        },
        name: {
          label: sntTranslate(marker("Nome")),
          value: "patient.name",
          resolve: (data) => data.patient?.name
        },
        species: {
          label: sntTranslate(marker("Specie")),
          value: "patient.species",
          resolve: (data) =>
            data.patient?.species
              ? sntTranslate(data.patient?.species)
              : undefined
        },
        breed: {
          label: sntTranslate(marker("Razza")),
          value: "patient.breed",
          resolve: (data) =>
            data.patient?.breed ? sntTranslate(data.patient?.breed) : undefined
        },
        birthday: {
          label: sntTranslate(marker("Data di nascita")),
          value: "patient.birthday",
          resolve: (data) =>
            data.patient?.birthday
              ? dateUtil(data.patient.birthday).format("DD/MM/YYYY")
              : undefined
        },
        microchip: {
          label: sntTranslate(marker("Microchip")),
          value: "patient.microchip",
          resolve: (data) => data.patient?.microchip
        },
        tattoo: {
          label: sntTranslate(marker("Tatuaggio")),
          value: "patient.tattoo",
          resolve: (data) => data.patient?.tattoo
        },
        sex: {
          label: sntTranslate(marker("Sesso")),
          value: "patient.sex",
          resolve: (data) =>
            data.patient?.sex === "male"
              ? sntTranslate(marker("Maschio"))
              : sntTranslate(marker("Femmina"))
        },
        size: {
          label: sntTranslate(marker("Taglia")),
          value: "patient.size",
          resolve: (data) => sntTranslate(data.patient?.size)
        },
        lastWeight: {
          label: sntTranslate(marker("Ultimo peso registrato")),
          value: "patient.lastWeight",
          resolve: (data) => {
            const formater = new Intl.NumberFormat("it-IT", {
              style: "unit",
              unit: "kilogram",
              unitDisplay: "short"
            });

            return data.patient?.weighing
              ? formater.format(last(data.patient.weighing)?.weight || 0) || ""
              : "";
          }
        },
        age: {
          label: sntTranslate(marker("Età")),
          value: "patient.age",
          resolve: (data) =>
            data.patient?.birthday
              ? dateUtilDurationHumanize(data.patient!.birthday)
              : undefined
        },
        physician: {
          label: sntTranslate(marker("Medico curante")),
          value: "patient.physician",
          resolve: (data) => getCustomerNominative(data.patient?.physician)
        },
        referringPhysicians: {
          label: sntTranslate(marker("Medico referente")),
          value: "patient.referringPhysicians",
          resolve: (data) =>
            data.patientReferringPhysician
              ? getCustomerNominative(data.patientReferringPhysician)
              : undefined
        },
        coat: {
          label: sntTranslate(marker("Pelo")),
          value: "patient.coat",
          resolve: (data) =>
            data.patient?.coat
              ? sntTranslate(marker(data.patient.coat + "-coat"))
              : ""
        },
        colors: {
          label: sntTranslate(marker("Mantello")),
          value: "patient.colors",
          resolve: (data) =>
            data.patient?.colors?.length > 0
              ? data.patient.colors.join(",")
              : ""
        }
      }
    },
    common: {
      label: sntTranslate(marker("Generali")),
      properties: {
        today: {
          label: sntTranslate(marker("Data di oggi")),
          value: "common.today",
          resolve: () => dateUtil().format("DD/MM/YYYY")
        }
      }
    },
    recovery: {
      label: sntTranslate(marker("Ricovero")),
      properties: {
        startDate: {
          label: sntTranslate(marker("Data inizio")),
          value: "recovery.startDate",
          resolve: (data: DocumentDataModel) =>
            data.recovery
              ? dateUtil(data.recovery?.startedAt).format("DD/MM/YYYY")
              : ""
        },
        endDate: {
          label: sntTranslate(marker("Data fine")),
          value: "recovery.endDate",
          resolve: (data: DocumentDataModel) =>
            data.recovery
              ? dateUtil(data.recovery?.startedAt).format("DD/MM/YYYY")
              : ""
        },
        anamnesis: {
          label: sntTranslate(marker("Anamnesi")),
          value: "recovery.anamnesis",
          resolve: (data: DocumentDataModel) =>
            data.ticket ? data.ticket.description : ""
        },
        cage: {
          label: sntTranslate(marker("Gabbia")),
          value: "recovery.cage",
          resolve: (data: DocumentDataModel) =>
            data.recovery ? data.recovery.cage || "" : ""
        },
        referringPhysician: {
          label: sntTranslate(marker("Medico referente")),
          value: "recovery.referringPhysician",
          resolve: (data: DocumentDataModel) =>
            data.recovery?.referringPhysician
              ? getCustomerNominative(data.recovery?.referringPhysician)
              : ""
        },
        urgency: {
          label: sntTranslate(marker("Codice di gravità")),
          value: "recovery.urgency",
          resolve: (data: DocumentDataModel) =>
            data.ticket ? sntTranslate(data.ticket?.priority) : ""
        },
        note: {
          label: sntTranslate(marker("Note")),
          value: "recovery.note",
          resolve: (data: DocumentDataModel) =>
            data.recovery ? data.recovery.note : ""
        }
      }
    },
    reminder: {
      label: sntTranslate(marker("Promemoria")),
      properties: {
        name: {
          label: sntTranslate(marker("Nome")),
          value: "reminder.name",
          resolve: (data: DocumentDataModel) => {
            return data.reminder?.name || "";
          }
        },
        expiresAtDate: {
          label: sntTranslate(marker("Data di scadenza")),
          value: "reminder.expiresAt",
          computable: () => true,
          computableValue: "expiresAt",
          computablePropertyName: "expiresAt",
          resolve: (data: DocumentDataModel) => {
            return data.reminder?.expiresAt
              ? dateUtil(data.reminder?.expiresAt).format("DD/MM/YYYY")
              : "";
          }
        },
        notificationAtDate: {
          label: sntTranslate(marker("Data di notifica")),
          value: "reminder.notificationAt",
          computable: () => true,
          computableValue: "notificationAt",
          computablePropertyName: "notificationAt",
          resolve: (data: DocumentDataModel) => {
            return data.reminder?.scheduledAt
              ? dateUtil(data.reminder?.scheduledAt).format("DD/MM/YYYY")
              : "";
          }
        }
      }
    }
  };
};

@Injectable()
export class DocumentService {
  private readonly templateService = inject(TemplateService);
  private readonly storageBackend = inject(StorageBackend);
  private readonly printService = inject(PrintService);
  private readonly letterheadBackend = inject(LetterheadBackend);
  private readonly tagBackend = inject(TagBackend);

  getModules(): DocumentModules {
    return MODULES();
  }

  getTypes(): DocumentTypes {
    return TYPES();
  }

  getModulesByText(text: string): ClinicalDocumentModule[] {
    const properties = this.getPropertiesByText(text);

    const modules = this.getModules();

    const modulesRet: ClinicalDocumentModule[] = [];

    toPairs(modules).forEach(([module, moduleConfig]) => {
      toPairs(moduleConfig.properties).forEach(([property, propertyConfig]) => {
        if (properties.includes(`{*${propertyConfig.value}}`)) {
          modulesRet.push(<ClinicalDocumentModule>module);
        }
      });
    });

    return uniq(modulesRet);
  }

  getPropertiesByText(text: string | undefined): string[] {
    if (!text) {
      return [];
    }

    return text.match(/\{\*[a-z.]+}/gim) || [];
  }

  getMustachePropertiesByText(text: string | undefined): string[] {
    if (!text) {
      return [];
    }

    return text.match(/\{\{[a-z.]+}}/gim) || [];
  }

  getPropertiesConfigByProperties(
    properties: string[],
    delimiterStart: string,
    delimiterEnd: string,
    useComputableValue?: boolean
  ): DocumentProperty[] {
    const ret: DocumentProperty[] = [];

    toPairs(MODULES()).forEach(([module, moduleConfig]) => {
      toPairs(moduleConfig.properties).forEach(([property, propertyConfig]) => {
        if (
          properties.includes(
            `${delimiterStart}${
              useComputableValue
                ? propertyConfig.computableValue
                : propertyConfig.value
            }${delimiterEnd}`
          )
        ) {
          ret.push(propertyConfig);
        }
      });
    });

    return ret;
  }

  async renderToHtml(
    text: string,
    dataModelProvider: DocumentDataModelProvider
  ): Promise<string> {
    const dataModel = await dataModelProvider();

    const properties = this.getPropertiesByText(text);
    const propertyConfigs = this.getPropertiesConfigByProperties(
      properties,
      "{*",
      "}"
    );

    let renderedHtml = text;

    for (const propertyConfig of propertyConfigs) {
      const propertyNameDelimited = `{\\*${propertyConfig.value}}`;
      let value = propertyConfig.resolve
        ? propertyConfig.resolve(dataModel) || ""
        : "";
      if (propertyConfig.value === "patient.colors" && value !== "") {
        const colorsID = value.split(",");
        const tagNames = await Promise.all(
          colorsID.map((id) =>
            lastValueFrom(this.tagBackend.load(id)).then((tag) => tag.name)
          )
        );
        value = tagNames.join(", ");
      }
      renderedHtml = renderedHtml.replace(
        new RegExp(propertyNameDelimited, "gi"),
        value
      );
    }

    return renderedHtml;
  }

  async renderWithComputedSupportToHtml(
    text: string,
    dataModelProvider: DocumentDataModelProvider
  ): Promise<{ rendered: string; computedProperties: Record<string, string> }> {
    const dataModel = await dataModelProvider();

    const properties = this.getPropertiesByText(text);
    const propertyConfigs = this.getPropertiesConfigByProperties(
      properties,
      "{*",
      "}"
    );

    let renderedHtml = text;

    const computedProperties: Record<string, string> = {};

    propertyConfigs.forEach((propertyConfig) => {
      const propertyNameDelimited = `{\\*${propertyConfig.value}}`;

      switch (true) {
        case propertyConfig.computable &&
          propertyConfig.computable(dataModel): {
          const propertyName =
            propertyConfig.computablePropertyName || propertyConfig.value;

          computedProperties[propertyName] =
            `$${propertyConfig.computableValue}`;

          const delimitedValue = `{{${propertyName}}}`;

          renderedHtml = renderedHtml.replace(
            new RegExp(propertyNameDelimited, "gi"),
            delimitedValue
          );
          break;
        }
        default: {
          const value = propertyConfig.resolve
            ? propertyConfig.resolve(dataModel) || ""
            : "";

          renderedHtml = renderedHtml.replace(
            new RegExp(propertyNameDelimited, "gi"),
            value
          );
          break;
        }
      }
    });

    return {
      computedProperties,
      rendered: renderedHtml
    };
  }

  async renderWithMustacheProperties(
    text: string,
    dataModelProvider: DocumentDataModelProvider
  ): Promise<string> {
    const dataModel = await dataModelProvider();

    const properties = this.getMustachePropertiesByText(text);
    const propertyConfigs = this.getPropertiesConfigByProperties(
      properties,
      "{{",
      "}}",
      true
    );

    let renderedHtml = text;

    propertyConfigs.forEach((propertyConfig) => {
      const propertyNameDelimited = `{{\s*${propertyConfig.computableValue}+\s*}}`;

      const value = propertyConfig.resolve
        ? propertyConfig.resolve(dataModel) || ""
        : "";

      renderedHtml = renderedHtml.replace(
        new RegExp(propertyNameDelimited, "gi"),
        value
      );
    });

    return renderedHtml;
  }

  async letterHeadDocunentToPdf(
    htmlContent: string,
    letterheadId?: string,
    additionalOptions?: {
      landscape?: boolean;
    }
  ): Promise<Blob> {
    switch (letterheadId) {
      case LETTERHEAD_FREE: {
        return this.printService.htmlToPdfNoHeaderFooterAndMargins(htmlContent);
      }
      default: {
        const letterhead: LetterheadLight | undefined = letterheadId
          ? await lastValueFrom(this.letterheadBackend.loadLight(letterheadId))
          : await lastValueFrom(this.letterheadBackend.loadDefaultLight());

        const headerFooter = letterhead
          ? {
              header: letterhead.header,
              footer: letterhead.footer?.html || ""
            }
          : undefined;

        const { logoData, secondaryImageData } = await lastValueFrom(
          forkJoin({
            logoData: iif(
              () => !!headerFooter?.header?.logo?.id,
              defer(() =>
                this.storageBackend
                  .download(headerFooter!.header!.logo!.id!)
                  .pipe(
                    switchMap((downloadUrl: string) =>
                      from(
                        fetch(downloadUrl)
                          .then((response) => response.blob())
                          .then(
                            (blob) => new Blob([blob], { type: "image/png" })
                          )
                          .then((imageBlob) => blobToDataUrl(imageBlob))
                      )
                    ),
                    catchError(() => of(undefined))
                  )
              ),
              of(undefined)
            ),
            secondaryImageData: iif(
              () => !!headerFooter?.header?.secondaryImage?.id,
              defer(() =>
                this.storageBackend
                  .download(headerFooter!.header!.secondaryImage!.id!)
                  .pipe(
                    switchMap((downloadUrl: string) =>
                      from(
                        fetch(downloadUrl)
                          .then((response) => response.blob())
                          .then(
                            (blob) => new Blob([blob], { type: "image/png" })
                          )
                          .then((imageBlob) => blobToDataUrl(imageBlob))
                      )
                    ),
                    catchError(() => of(undefined))
                  )
              ),
              of(undefined)
            )
          })
        );

        const tailwindCss = await fetch(
          "/assets/ejs-templates/invoice/tailwind.css"
        ).then((response) => response.text());

        const [headerHTML, footerHTML] = await Promise.all([
          fetch("/assets/ejs-templates/header.ejs")
            .then((response) => response.text())
            .then((headerEjs) => {
              return this.templateService.render(headerEjs, {
                styles: tailwindCss,
                content: headerFooter?.header?.html || "",
                title: "",
                logo: logoData,
                secondaryImage: secondaryImageData,
                logoSize: letterhead?.header?.logo?.size || 1,
                secondaryImageSize:
                  letterhead?.header?.secondaryImage?.size || 1
              });
            }),
          fetch("/assets/ejs-templates/footer.ejs")
            .then((response) => response.text())
            .then((footerEjs) => {
              return this.templateService.render(footerEjs, {
                styles: tailwindCss,
                content: headerFooter?.footer || ""
              });
            })
        ]);

        return this.printService.htmlToPdf({
          header: headerHTML,
          footer: footerHTML,
          content: htmlContent,
          additionalOptions
        });
      }
    }
  }
}
